import { useCallback } from 'react';
import { useEmployeeSuggestionsService } from 'domain/shared/employee';
import {
  type FilterBarFilterOption,
  type FilterBarFiltersProps,
  type FilterBarQuickFilter,
  type FilterBarSearchInputProps,
} from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import { POSITION_FILL_REQUEST_STATUS, POSITION_FILL_REQUEST_STATUS_LABELS } from '../constants';
import type { PositionFillRequestsFilters } from '../types';
import useBusinessFunctionService from 'domain/shared/businessFunction/businessFunctionService';
import { useAsyncValue } from 'framework/utils';

interface UsePositionFillRequestsFilterBarParams {
  filters: PositionFillRequestsFilters;
  searchValue: string;
  setSearchValue: (value: string) => void;
  setFilters: (filters: PositionFillRequestsFilters) => void;
  resetFilters: () => void;
}

const mapEmployeeOption = (employee: any): FilterBarFilterOption => ({
  value: employee.alias,
  label: [employee.alias, employee.nameEn?.firstName, employee.nameEn?.lastName].filter(Boolean).join(' '),
});

const usePositionFillRequestsFilterBar = ({
  filters,
  searchValue,
  setSearchValue,
  setFilters,
  resetFilters,
}: UsePositionFillRequestsFilterBarParams): {
  searchProps: FilterBarSearchInputProps;
  filterProps: FilterBarFiltersProps;
} => {
  const { searchWithContractors } = useEmployeeSuggestionsService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { getUserAlias } = useAuthenticationProvider();
  const currentUserAlias = getUserAlias();

  const businessFunctionOptions = useAsyncValue(findAllBusinessFunctions, []) || [];

  const loadOwnerOptions = useCallback(
    async (search: string) => {
      const response = await searchWithContractors({
        filter: search ? { searchString: { ct: search } } : {},
      });
      return (response.result ?? []).map(mapEmployeeOption);
    },
    [searchWithContractors],
  );

  const resolveOwnerOption = useCallback(
    async (alias: string) => {
      const response = await searchWithContractors({ filter: { alias: { eq: alias } } });
      const employee = response.result?.[0];
      return employee ? mapEmployeeOption(employee) : { value: alias, label: alias };
    },
    [searchWithContractors],
  );

  const quickFilters: FilterBarQuickFilter[] = [
    {
      id: 'activeRequests',
      label: 'Active requests',
      state: {
        status: { [FilterCondition.IN]: [POSITION_FILL_REQUEST_STATUS.ACTIVE] },
      },
    },
    {
      id: 'myRequests',
      label: 'My requests',
      disabled: !currentUserAlias,
      state: {
        status: {
          [FilterCondition.IN]: [
            POSITION_FILL_REQUEST_STATUS.REQUESTED,
            POSITION_FILL_REQUEST_STATUS.PENDING,
            POSITION_FILL_REQUEST_STATUS.ACTIVE,
            POSITION_FILL_REQUEST_STATUS.ALLOCATED,
          ],
        },
        ownerAlias: { [FilterCondition.EQ]: currentUserAlias },
      },
    },
  ];

  return {
    searchProps: {
      value: searchValue,
      onChange: setSearchValue,
    },
    filterProps: {
      value: filters,
      onChange: nextFilters => {
        setFilters(nextFilters as PositionFillRequestsFilters);
      },
      onReset: () => {
        resetFilters();
      },
      quickFilters,
      filters: [
        {
          id: 'status',
          source: 'status',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Status',
          multiple: true,
          options: Object.values(POSITION_FILL_REQUEST_STATUS).map(status => ({
            value: status,
            label: POSITION_FILL_REQUEST_STATUS_LABELS[status],
          })),
        },
        {
          id: 'ownerAlias',
          source: 'ownerAlias',
          condition: FilterCondition.EQ,
          type: 'autosuggestion',
          label: 'Owner',
          minSearchLength: 1,
          loadOptions: loadOwnerOptions,
          resolveOption: resolveOwnerOption,
        },
        {
          id: 'positionSlot.businessFunction',
          source: 'positionSlot.businessFunctionId',
          condition: FilterCondition.IN,
          type: 'dropdown',
          label: 'Business Function',
          multiple: true,
          options: businessFunctionOptions.map(bf => ({ value: bf.id, label: bf.name })),
        },
      ],
    },
  };
};

export default usePositionFillRequestsFilterBar;
